import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';

export const COMPONENT_ID = 'company_name';

const GET_COMPANY_QUERY = gql`
  query company($id: ID!) {
    company(id: $id) {
      id
      name
    }
  }
`;

const CompanyNameView = ({ data, sx }: ComponentViewProps<string>) => {
  return <Box sx={{ ...sx }}>{data || 'Company Name'}</Box>;
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<string>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);
  const { loading, error } = useQuery(GET_COMPANY_QUERY, {
    variables: { id: portfolio?.companies?.[0] },
    skip: !!persistedData,
    onCompleted: data => {
      if (data?.company?.name) {
        setDataFetched(data?.company?.name);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: data?.company?.name,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const CompanyName = ({
  portfolio,
  persistedData,
  onComponentLoad,
  sx,
}: ComponentEditProps<string>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <CompanyNameView sx={sx} data={data} />}
    </LoadingErrorWrapper>
  );
};

export const CompanyNameComponent: ComponentDefinition<string> = {
  id: COMPONENT_ID,
  name: 'Company Name',
  type: SmartReportComponentTypesEnum.TextField,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: CompanyName,
  Component: CompanyName,
  ComponentView: CompanyNameView,
};
